import { Check, ChevronDown } from 'lucide-react';
import { Select as RadixSelect } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { menuItem, menuSurface } from './menu-styles';

export interface SelectOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Plain text for type-ahead when `label` is rich. */
  text?: string;
  disabled?: boolean;
}

export interface SelectProps<T extends string> {
  value: T | undefined;
  options: readonly SelectOption<T>[];
  onValueChange: (value: T) => void;
  id?: string;
  placeholder?: string;
  'aria-label'?: string;
  'aria-describedby'?: string | undefined;
  'aria-invalid'?: boolean;
  className?: string;
  disabled?: boolean;
}

export function Select<T extends string>({
  value,
  options,
  onValueChange,
  id,
  placeholder,
  className,
  disabled,
  ...aria
}: SelectProps<T>) {
  return (
    <RadixSelect.Root
      {...(value === undefined ? {} : { value })}
      onValueChange={(next) => {
        const option = options.find((o) => o.value === next);
        if (option) onValueChange(option.value);
      }}
      {...(disabled === undefined ? {} : { disabled })}
    >
      <RadixSelect.Trigger
        id={id}
        aria-label={aria['aria-label']}
        aria-describedby={aria['aria-describedby']}
        aria-invalid={aria['aria-invalid']}
        className={cn(
          'inline-flex h-7 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-border-strong bg-bg-2 px-2 text-left text-sm text-text-1',
          'outline-none hover:border-text-4 focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent-subtle',
          'disabled:opacity-50 aria-invalid:border-status-critical data-[placeholder]:text-text-4',
          className,
        )}
      >
        <span className="truncate">
          <RadixSelect.Value {...(placeholder === undefined ? {} : { placeholder })} />
        </span>
        <RadixSelect.Icon>
          <ChevronDown className="size-3.5 text-text-3" />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          sideOffset={4}
          collisionPadding={8}
          className={cn(
            menuSurface,
            'max-h-[min(var(--radix-select-content-available-height),320px)] min-w-[var(--radix-select-trigger-width)]',
          )}
        >
          <RadixSelect.Viewport>
            {options.map((option) => (
              <RadixSelect.Item
                key={option.value}
                value={option.value}
                {...(option.text === undefined ? {} : { textValue: option.text })}
                {...(option.disabled === undefined ? {} : { disabled: option.disabled })}
                className={cn(menuItem, 'pr-7')}
              >
                <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
                <RadixSelect.ItemIndicator className="absolute right-2 inline-flex">
                  <Check className="!text-text-1" />
                </RadixSelect.ItemIndicator>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}
